import { useState, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';
import { RefreshCw, Zap, Plus, Trash2, Fingerprint, Server, CheckCircle2, AlertCircle, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { usePunchLogs, usePunchDevices, useAddPunchLog, useDeletePunchLog, useSyncPunchLogs, useProcessPunchLogs } from '@/api/queries/punch-log.queries';
import { useEmployees } from '@/api/queries/employee.queries';
import { StatCard } from '@/components/shared/StatCard';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 50;

const STATUS_CHIP = {
  PROCESSED:   { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',   label: 'Processed'   },
  UNPROCESSED: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',   label: 'Unprocessed' },
  IGNORED:     { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',            label: 'Ignored'     },
};

const TYPE_CHIP = {
  IN:  { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'IN'  },
  OUT: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',              label: 'OUT' },
};

function AddPunchModal({ devices, employees, onClose, onSaved }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [form, setForm] = useState({
    employee_id: '', employee_code: '', employee_name: '', department: '',
    device_id: devices[0]?.id || 'device-001', date: today, time: '09:00',
    punch_type: 'IN', source: 'BIOMETRIC',
  });
  const [empSearch, setEmpSearch] = useState('');
  const [empOpen, setEmpOpen] = useState(false);
  const { mutate: addPunch, isPending } = useAddPunchLog();

  const empList = useMemo(() => {
    const emps = employees?.data || [];
    if (!empSearch) return emps.slice(0, 20);
    return emps.filter(e => {
      const name = `${e.first_name} ${e.last_name}`.toLowerCase();
      return name.includes(empSearch.toLowerCase()) || e.employee_code?.toLowerCase().includes(empSearch.toLowerCase());
    }).slice(0, 20);
  }, [employees, empSearch]);

  const selectEmployee = (emp) => {
    setForm(f => ({
      ...f,
      employee_id: emp.id,
      employee_code: emp.employee_code,
      employee_name: `${emp.first_name} ${emp.last_name}`,
      department: emp.department?.name || emp.department || '',
    }));
    setEmpOpen(false);
    setEmpSearch('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    addPunch(form, {
      onSuccess: () => { toast.success('Punch added'); onSaved(); onClose(); },
      onError: () => toast.error('Failed to add punch'),
    });
  };

  const devName = devices.find(d => d.id === form.device_id)?.name || form.device_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2.5">
            <Fingerprint size={18} className="text-[var(--accent)]"/>
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">Add Punch</h2>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">
            <X size={15}/>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Employee picker */}
          <div className="relative">
            <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Employee *</label>
            <div
              onClick={() => setEmpOpen(v => !v)}
              className="flex items-center gap-2 h-9 px-3 border border-[var(--border)] rounded-lg cursor-pointer hover:border-[var(--accent)] text-[13px]">
              {form.employee_name
                ? <span className="text-[var(--text-primary)]">{form.employee_name} <span className="text-[var(--text-tertiary)]">· {form.employee_code}</span></span>
                : <span className="text-[var(--text-tertiary)]">Select employee…</span>}
            </div>
            {empOpen && (
              <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-[var(--shadow-lg)]">
                <div className="p-2 border-b border-[var(--border)]">
                  <input
                    autoFocus
                    value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                    placeholder="Search…"
                    className="w-full h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"
                  />
                </div>
                <div className="max-h-44 overflow-y-auto py-1">
                  {empList.map(emp => (
                    <button key={emp.id} type="button" onClick={() => selectEmployee(emp)}
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--bg-hover)]">
                      <span className="font-medium text-[var(--text-primary)]">{emp.first_name} {emp.last_name}</span>
                      <span className="text-[var(--text-tertiary)] ml-2">{emp.employee_code}</span>
                    </button>
                  ))}
                  {empList.length === 0 && <p className="px-3 py-3 text-[12px] text-[var(--text-tertiary)]">No employees found</p>}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full h-9 px-3 text-[13px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Time *</label>
              <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full h-9 px-3 text-[13px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Punch Type</label>
            <div className="flex gap-2">
              {['IN', 'OUT'].map(t => (
                <button key={t} type="button" onClick={() => setForm(f => ({ ...f, punch_type: t }))}
                  className={cn('flex-1 h-9 rounded-lg text-[13px] font-medium border transition-all', form.punch_type === t
                    ? t === 'IN' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-blue-500 text-white border-blue-500'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Device</label>
              <select value={form.device_id} onChange={e => setForm(f => ({ ...f, device_id: e.target.value }))}
                className="w-full h-9 px-3 text-[13px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
                {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[var(--text-secondary)] mb-1.5">Source</label>
              <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                className="w-full h-9 px-3 text-[13px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
                <option value="BIOMETRIC">Biometric</option>
                <option value="MANUAL">Manual</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 h-9 border border-[var(--border)] rounded-lg text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              Cancel
            </button>
            <button type="submit" disabled={isPending}
              className="flex-1 h-9 bg-[var(--accent)] text-white rounded-lg text-[13px] font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
              {isPending ? 'Adding…' : 'Add Punch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PunchLogPage() {
  const companyName = useSelector(selectCompanyName);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ date_from: format(subDays(new Date(), 29), 'yyyy-MM-dd'), date_to: today });
  const [empSearch, setEmpSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const params = useMemo(() => ({
    ...filters,
    ...(empSearch ? { search: empSearch } : {}),
    page, limit: PAGE_SIZE,
  }), [filters, empSearch, page]);

  const { data: empData }     = useEmployees({ status: 'ACTIVE', limit: 500 });
  const realEmployees         = useMemo(() => empData?.data || [], [empData]);
  const { data: logsData, isLoading, refetch } = usePunchLogs(params, realEmployees);
  const { data: devicesData } = usePunchDevices();
  const { mutate: deletePunch } = useDeletePunchLog();
  const { mutate: sync, isPending: syncing } = useSyncPunchLogs();
  const { mutate: process, isPending: processing } = useProcessPunchLogs();

  const logs   = logsData?.data || [];
  const meta   = logsData?.meta || { total: 0, page: 1, pages: 1 };
  const devices = devicesData?.data || [];

  const totalLogs    = meta.total;
  const processed    = logs.filter(l => l.status === 'PROCESSED').length;
  const unprocessed  = logs.filter(l => l.status === 'UNPROCESSED').length;

  const handleSync = () => {
    // Phase 1B: no biometric device connected — sync just returns 0
    sync({}, {
      onSuccess: (res) => {
        const n = res?.data?.added ?? 0;
        if (n === 0) {
          toast.info(res?.data?.message || 'No biometric device connected. Use + Add Punch to record punches manually.');
        } else {
          toast.success(`Synced ${n} new punch records — click Process Logs to record attendance`);
        }
        refetch();
      },
      onError: () => toast.error('Sync failed'),
    });
  };

  const handleProcess = () => {
    process(undefined, {
      onSuccess: (res) => {
        const n = res?.data?.processed ?? 0;
        toast.success(`Processed ${n} records → attendance updated`);
        refetch();
      },
      onError: () => toast.error('Processing failed'),
    });
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    deletePunch(id, {
      onSuccess: () => { toast.success('Deleted'); setDeletingId(null); refetch(); },
      onError:   () => { toast.error('Delete failed'); setDeletingId(null); },
    });
  };

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-0.5">
            <Fingerprint size={20} className="text-[var(--accent)]"/>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">Punch Log</h1>
          </div>
          <p className="text-[13px] text-[var(--text-secondary)]">Biometric device IN/OUT punches — {companyName}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2 h-9 px-4 border border-[var(--border)] rounded-lg text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-60 transition-all">
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''}/>
            {syncing ? 'Syncing…' : 'Sync Devices'}
          </button>
          <button onClick={handleProcess} disabled={processing}
            className="flex items-center gap-2 h-9 px-4 border border-[var(--border)] rounded-lg text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-60 transition-all">
            <Zap size={14}/>
            {processing ? 'Processing…' : 'Process Logs'}
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white rounded-lg text-[13px] font-semibold hover:opacity-90 transition-opacity">
            <Plus size={14}/>
            Add Punch
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Punches"    value={totalLogs.toLocaleString()}  icon={Fingerprint} color="blue"/>
        <StatCard title="Processed"        value={processed.toLocaleString()}  icon={CheckCircle2} color="green"/>
        <StatCard title="Unprocessed"      value={unprocessed.toLocaleString()} icon={AlertCircle} color="yellow"/>
        <StatCard title="Devices Active"   value={devices.length.toString()}   icon={Server} color="purple"/>
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <Search size={14} className="text-[var(--text-tertiary)] shrink-0"/>
          <input
            placeholder="Search employee…"
            value={empSearch}
            onChange={e => { setEmpSearch(e.target.value); setPage(1); }}
            className="flex-1 text-[13px] bg-transparent outline-none placeholder:text-[var(--text-tertiary)]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={filters.date_from || ''}
            onChange={e => setFilter('date_from', e.target.value)}
            className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
          <input type="date" value={filters.date_to || ''}
            onChange={e => setFilter('date_to', e.target.value)}
            className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
          <select value={filters.device_id || ''}
            onChange={e => setFilter('device_id', e.target.value || undefined)}
            className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
            <option value="">All Devices</option>
            {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={filters.punch_type || ''}
            onChange={e => setFilter('punch_type', e.target.value || undefined)}
            className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
            <option value="">IN + OUT</option>
            <option value="IN">IN only</option>
            <option value="OUT">OUT only</option>
          </select>
          <select value={filters.status || ''}
            onChange={e => setFilter('status', e.target.value || undefined)}
            className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
            <option value="">All Status</option>
            <option value="PROCESSED">Processed</option>
            <option value="UNPROCESSED">Unprocessed</option>
            <option value="IGNORED">Ignored</option>
          </select>
          {Object.values(filters).some(Boolean) && (
            <button onClick={() => { setFilters({}); setEmpSearch(''); setPage(1); }}
              className="h-8 px-2.5 text-[12px] border border-[var(--border)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] flex items-center gap-1.5">
              <X size={11}/> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                {['Date', 'Time', 'Employee', 'Code', 'Department', 'Device', 'Type', 'Source', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-[var(--border)] last:border-0">
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-[var(--bg-hover)] rounded animate-pulse w-20"/></td>
                    ))}
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-[var(--text-tertiary)]">
                    <Fingerprint size={32} className="mx-auto mb-2 opacity-30"/>
                    No punch logs for the selected filters
                  </td>
                </tr>
              ) : logs.map(log => {
                const statusChip = STATUS_CHIP[log.status] || STATUS_CHIP.PROCESSED;
                const typeChip   = TYPE_CHIP[log.punch_type] || TYPE_CHIP.IN;
                const timeStr    = log.punch_time ? new Date(log.punch_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
                return (
                  <tr key={log.id} className={cn('border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors group', log.status === 'UNPROCESSED' && 'bg-amber-50/40 dark:bg-amber-900/10')}>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{log.date}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap tabular-nums">{timeStr}</td>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{log.employee_name}</td>
                    <td className="px-4 py-3 text-[var(--text-tertiary)] whitespace-nowrap font-mono text-[12px]">{log.employee_code}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{log.department}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{log.device_name}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold', typeChip.cls)}>
                        {typeChip.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-tertiary)] whitespace-nowrap">{log.source}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold', statusChip.cls)}>
                        {statusChip.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(log.id)}
                        disabled={deletingId === log.id}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 hover:text-red-600 text-[var(--text-tertiary)] transition-all disabled:opacity-40">
                        <Trash2 size={13}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border)] bg-[var(--bg-app)]">
            <p className="text-[12px] text-[var(--text-tertiary)]">
              {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, meta.total)} of {meta.total.toLocaleString()} punches
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-[var(--border)] hover:bg-[var(--bg-hover)] disabled:opacity-40">
                <ChevronLeft size={13}/>
              </button>
              <span className="px-3 text-[12px] text-[var(--text-secondary)]">Page {page} / {meta.pages}</span>
              <button onClick={() => setPage(p => Math.min(meta.pages, p + 1))} disabled={page >= meta.pages}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-[var(--border)] hover:bg-[var(--bg-hover)] disabled:opacity-40">
                <ChevronRight size={13}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {showAdd && (
        <AddPunchModal
          devices={devices}
          employees={empData}
          onClose={() => setShowAdd(false)}
          onSaved={() => refetch()}
        />
      )}
    </div>
  );
}
